import React, { useState, useEffect, useRef, useCallback } from "react";
import "./realizations.scss";

interface RealizationItem {
  id: number;
  image: string;
}

export const realizationsData: RealizationItem[] = [
  {
    id: 1,
    image: "images/realizations/floor.jpg",
  },
  {
    id: 2,
    image: "/images/realizations/interior2.jpg",
  },
  {
    id: 3,
    image: "/images/realizations/interior.jpg",
  },
  {
    id: 4,
    image: "/images/realizations/plaster-finish.jpg",
  },
  {
    id: 5,
    image: "/images/realizations/stairs.jpg",
  },
  {
    id: 6,
    image: "/images/realizations/stairs2.jpg",
  },
  {
    id: 7,
    image: "/images/realizations/wall.jpg",
  },
  {
    id: 8,
    image: "/images/realizations/wall2.jpg",
  },
  {
    id: 9,
    image: "/images/realizations/window.jpg",
  },
];

const Realizations: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  const dragStartX = useRef<number | null>(null);
  const isDragging = useRef<boolean>(false);

  // Update visible card count based on screen width
  const updateItemsPerPage = useCallback(() => {
    const width = window.innerWidth;
    if (width < 640) {
      setItemsPerPage(1);
    } else if (width < 1024) {
      setItemsPerPage(2);
    } else {
      setItemsPerPage(3);
    }
  }, []);

  useEffect(() => {
    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, [updateItemsPerPage]);

  const maxIndex = Math.max(0, realizationsData.length - itemsPerPage);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  // Autoplay functionality
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      handleNext();
    }, 4500);

    return () => clearInterval(interval);
  }, [isPaused, handleNext]);

  // Mouse Drag & Touch Swipe Handlers
  const handleDragStart = (clientX: number) => {
    dragStartX.current = clientX;
    isDragging.current = true;
    setIsPaused(true);
  };

  const handleDragEnd = (clientX: number) => {
    if (!isDragging.current || dragStartX.current === null) return;
    const distance = dragStartX.current - clientX;
    const minSwipeThreshold = 40;

    if (distance > minSwipeThreshold) {
      handleNext();
    } else if (distance < -minSwipeThreshold) {
      handlePrev();
    }

    dragStartX.current = null;
    isDragging.current = false;
  };

  // Calculate total dot pages
  const totalPages = realizationsData.length - itemsPerPage + 1;

  return (
    <section className="realizations">
      <div className="realizations__header">
        <span className="realizations__header-line"></span>
        <h2 className="realizations__title">NASZE REALIZACJE</h2>
        <span className="realizations__header-line"></span>
      </div>

      <div
        className="realizations__slider-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => {
          setIsPaused(false);
          isDragging.current = false;
        }}
      >
        {/* Navigation Arrow Left */}
        <button
          className="realizations__nav-btn realizations__nav-btn--prev"
          onClick={handlePrev}
          aria-label="Poprzednia realizacja"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Carousel Track */}
        <div
          className="realizations__track-wrapper"
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseUp={(e) => handleDragEnd(e.clientX)}
        >
          <div
            className="realizations__track"
            style={{
              transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)`,
            }}
          >
            {realizationsData.map((item) => (
              <div
                key={item.id}
                className="realizations__slide"
                style={{ flex: `0 0 ${100 / itemsPerPage}%` }}
              >
                <div className="realizations__card">
                  <div className="realizations__image-wrapper">
                    <img
                      src={item.image}
                      alt={item.image}
                      className="realizations__image"
                      loading="lazy"
                      draggable={false}
                    />
                    <div className="realizations__overlay" />
                  </div>
                  <div className="realizations__card-content"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrow Right */}
        <button
          className="realizations__nav-btn realizations__nav-btn--next"
          onClick={handleNext}
          aria-label="Następna realizacja"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Pagination Dots */}
      <div className="realizations__dots">
        {Array.from({ length: totalPages }).map((_, dotIndex) => (
          <button
            key={dotIndex}
            className={`realizations__dot ${
              dotIndex === currentIndex ? "realizations__dot--active" : ""
            }`}
            onClick={() => setCurrentIndex(dotIndex)}
            aria-label={`Przejdź do slajdu ${dotIndex + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Realizations;
